import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import React, { useState, useEffect } from "react";
import "chart.js/auto";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import GroupIcon from "@mui/icons-material/Group";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { useSelector } from "react-redux";
import { useFetch, useUpdate } from "../../hooks";
import { urls } from "../../urls/urls";
import { BASE_URL_TEST } from "../../utils/baseUrl";

export default function OverView() {
  const [timeFrame, setTimeFrame] = useState("This Week");
  const [metricsData, setMetricsData] = useState(null);
  const [revenueOverviewData, setRevenueOverviewData] = useState(null);
  const apptoken = useSelector((state) => state.user.apptoken);

  // Map timeFrame to API filter values
  const getFilterValue = (timeFrame) => {
    switch (timeFrame) {
      case "This Week":
        return "week";
      case "This Month":
        return "month";
      case "This Year":
        return "year";
      default:
        return "week";
    }
  };

  const { data, isLoading, fetchData } = useFetch(
    BASE_URL_TEST + urls("getOverviewMetrics"),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: apptoken ? `Bearer ${apptoken}` : "",
      },
    },
  );

  const {
    data: revenueData,
    isLoading: revenueLoading,
    fetchData: fetchRevenueData,
  } = useFetch(
    BASE_URL_TEST +
      urls("getrevenueOverview") +
      `?filter=${getFilterValue(timeFrame)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: apptoken ? `Bearer ${apptoken}` : "",
      },
    },
  );

  useEffect(() => {
    fetchData();
    fetchRevenueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data && data.status) {
      setMetricsData(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (revenueData && revenueData.status) {
      setRevenueOverviewData(revenueData.data);
    }
  }, [revenueData]);

  useEffect(() => {
    fetchRevenueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFrame]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const summaryData = metricsData
    ? [
        {
          title: "Total Revenue",
          value: formatCurrency(metricsData.total_revenue),
          growth: `${metricsData.total_orders_count} Orders`,
          color: "#4CAF50",
          icon: <AttachMoneyIcon fontSize="large" />,
        },
        {
          title: "Payments",
          value: formatCurrency(metricsData.total_order_payments),
          growth: `${metricsData.total_orders_count} Transactions`,
          color: "#1E88E5",
          icon: <CreditCardIcon fontSize="large" />,
        },
        {
          title: "Regular Vendor",
          value: metricsData.regular_vendors_count,
          growth: `${metricsData.total_vendors_count} Total Vendors`,
          color: "#26A69A",
          icon: <GroupIcon fontSize="large" />,
        },
        {
          title: "Listing Vendor",
          value: metricsData.listing_vendors_count,
          growth: `${metricsData.total_vendors_count} Total Vendors`,
          color: "#4CAF50",
          icon: <MonetizationOnIcon fontSize="large" />,
        },
      ]
    : [];

  // Get labels based on filter type (week uses 'day', month/year uses 'month')
  const getChartLabels = () => {
    if (!revenueOverviewData?.data) return [];
    return revenueOverviewData.data.map((item) => item.day || item.month || "");
  };

  const chartData = revenueOverviewData
    ? {
        labels: getChartLabels(),
        datasets: [
          {
            label: "Listing Revenue",
            data:
              revenueOverviewData.data?.map((item) => item.listing_amount) ||
              [],
            backgroundColor: "#4CAF50",
            barThickness: revenueOverviewData.filter === "year" ? 15 : 20,
          },
          {
            label: "Cart Revenue",
            data:
              revenueOverviewData.data?.map((item) => item.cart_amount) || [],
            backgroundColor: "#64B5F6",
            barThickness: revenueOverviewData.filter === "year" ? 15 : 20,
          },
        ],
      }
    : {
        labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        datasets: [
          {
            label: "Listing Revenue",
            data: [0, 0, 0, 0, 0, 0, 0],
            backgroundColor: "#4CAF50",
            barThickness: 20,
          },
          {
            label: "Cart Revenue",
            data: [0, 0, 0, 0, 0, 0, 0],
            backgroundColor: "#64B5F6",
            barThickness: 20,
          },
        ],
      };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 20,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  if (isLoading || revenueLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          bgcolor: "#EEF2FF",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 0,
        bgcolor: "#EEF2FF",
      }}
    >
      <Grid container spacing={1}>
        {summaryData.map((item, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                borderRadius: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                bgcolor: item.color,
                color: "#fff",
              }}
            >
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  justifyContent={"space-between"}
                >
                  <Box>
                    <Typography variant="body1">{item.title}</Typography>
                    <Typography variant="h6">{item.value}</Typography>
                    <Typography variant="body2">{item.growth}</Typography>
                  </Box>
                  <Typography variant="h3">{item.icon}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          p: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Revenue Overview</Typography>
          <Select
            value={timeFrame}
            onChange={(e) => setTimeFrame(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ bgcolor: "#F5F5F5", borderRadius: "8px" }}
          >
            <MenuItem value="This Week">This Week</MenuItem>
            <MenuItem value="This Year">This Year</MenuItem>
          </Select>
        </Box>
        {(revenueOverviewData || metricsData) && (
          <>
            <Typography variant="h4" color="primary">
              {formatCurrency(
                revenueOverviewData?.total_revenue ||
                  metricsData?.total_revenue ||
                  0,
              )}
            </Typography>
            <Typography variant="body2">
              {revenueOverviewData?.period ||
                `${metricsData?.total_orders_count || 0} Orders`}
            </Typography>
          </>
        )}
        <Box sx={{ height: 300, mt: 2 }}>
          <Bar data={chartData} options={chartOptions} />
        </Box>
      </Card>
    </Box>
  );
}
